import type { AircraftCategory } from '@/lib/db';

/**
 * Flatten the register's category wording into one of ours.
 *
 * The categories are the CAA's own, so this is mostly a matter of dropping the
 * qualifiers the register carries and the app does not: "Amateur Built
 * Aeroplane" is an aeroplane, "Microlight Class 1" and "Class 2" are both a
 * microlight, "Power Glider" is a glider, and a gyroplane is its own thing
 * rather than a helicopter, which is why it stopped being filed as one.
 *
 * What it will not do is guess at engine count. Nothing in the register says
 * whether an aeroplane is a single or a twin, and that is the field the
 * logbook's hour column is derived from: "Twin Comanche" is a twin, "Twin
 * Otter" is a twin, "Twin Star" is a motor glider, and a Cessna 337's name
 * says nothing at all. A wrong engine count moves hours into the wrong logbook
 * column, which is the one mistake in this file that reaches a pilot's licence.
 *
 * Order matters below: every "Amateur Built X" and "Power Glider" has to meet
 * its own pattern before the generic aeroplane one.
 */

const PATTERNS: readonly (readonly [RegExp, AircraftCategory])[] = [
  [/gyroplane|gyrocopter|autogiro|autogyro/i, 'gyroplane'],
  [/helicopter|rotorcraft/i, 'helicopter'],
  [/glider|sailplane/i, 'glider'],
  [/microlight|micro-light|ultralight/i, 'microlight'],
  [/balloon|airship|dirigible/i, 'balloon'],
  [/aeroplane|airplane|aircraft|powered|landplane|seaplane|amphibian/i, 'aeroplane'],
];

/**
 * Map the register's category wording to a category of ours.
 *
 * @returns the category, or `undefined` when the wording carries nothing this
 *          app is prepared to claim.
 */
export function suggestCategory(category: string | undefined): AircraftCategory | undefined {
  if (category === undefined) {
    return undefined;
  }
  for (const [pattern, matched] of PATTERNS) {
    if (pattern.test(category)) {
      return matched;
    }
  }
  return undefined;
}
